import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #19: It’s hard to package a library",
  "description": "",
  "date": "2016-08-15T08:00:00.000Z",
  "lastUpdated": "2016-08-15T08:00:00.000Z",
  "image": ""
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding ?. Always under 500 words and with pictures. You can follow my channel, `}<a parentName="em" {...{
          "href": "https://www.youtube.com/user/TheSwizec/"
        }}>{`here`}</a>{`. New content almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{`. There’s live chat, come say hai ?`}</em></p>
    <p>{`This week, we wanted to build an example for the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-18-an-abstract-react-transition-component/swizec/6906"
      }}>{`TransitionableComponent from last week`}</a>{` — an animated piechart. Not because piecharts are exciting, but because someone asked me by email.`}</p>
    <p>{`It did not go so well. After 1 hour and 45 minutes of fighting everything from Webpack to npm, we built this:`}</p>
    <p><img parentName="p" {...{
        "src": "http://swizec.com/blog/wp-content/uploads/2016/08/Screen-Shot-2016-08-14-at-23.13.04.png",
        "alt": "A not animated piechart"
      }}></img></p>
    <p>{`A `}<em parentName="p">{`not`}</em>{` animated piechart that shows the ratio of British imports vs. exports in the year 1820. Exports are blue. The tiny dataset is from `}<a parentName="p" {...{
        "href": "http://www.stat.ncsu.edu/research/sas/sicl/data/"
      }}>{`A Handbook of Small Data Sets`}</a>{`.`}</p>
    <p>{`We were going to build an animation that walks through the years in our dataset – 1820 through 1850 – and draws a 2-arc piechart for each year. If there was time, we’d add the ability for users to scroll through.`}</p>
    <p>{`Building the piechart itself was quick: Fetch and parse data with `}<inlineCode parentName="p">{`d3.tsv('data.dat')`}</inlineCode>{`, call `}<inlineCode parentName="p">{`setState`}</inlineCode>{` and build a 2-entry array as data for the piechart. Our tiny dataset gives only one number, so we have to infer the other.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/App.js

componentDidMount() {
        d3.tsv('data.dat')
          .row(({imports, year}) => ({imports: Number(imports),
                                      year: Number(year)}))
          .get((data) => {
              this.setState({data: data,
                             currentIndex: 0});
          });
    }

    render() {
        let pie = null;

        if (this.state.data.length) {
            const yearData = this.state.data[this.state.currentIndex],
                  pieData = [{value: yearData.imports},
                             {value: 100-yearData.imports}];

            pie = <animatedpiechart data={pieData} x="400" y="300" r="100">;
        }

        // return an svg, add the pie, etc
    }
}
</animatedpiechart>
`}</code></pre>
    <p>{`That’s how you’d use the `}<inlineCode parentName="p">{`AnimatedPiechart`}</inlineCode>{` component once it works. The component itself wasn’t quick to build, too. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/AnimatedPiechart.js

class Arc extends Component { // this would be TransitionableComponent
    render() {
        const arc = d3.arc()
                      .innerRadius(this.props.innerRadius)
                      .outerRadius(this.props.outerRadius);

        return (
            <path d={arc(this.props.data)} style="{{fill:" this.props.color}}="">
        );
    }
}

const AnimatedPiechart = ({ x, y, r, data }) => {
    let pie = d3.pie()
                .value((d) => d.value)(data),
        translate = \`translate(\${x}, \${y})\`,
        colors = d3.scaleOrdinal(d3.schemeCategory10);

    return (
        <g transform={translate}>
            {pie.map((d, i) => (
                <arc key={\`arc-\${i}\`} data={d} innerradius="0" outerradius={r} color={colors(i)}>
             ))}
        </arc></g>
    );
};
</path>
`}</code></pre>
    <p>{`A piechart is a collection of arcs. As such it doesn’t need to do much: use `}<inlineCode parentName="p">{`d3.pie()`}</inlineCode>{` to turn a dataset into a piechart, then walk through it and render arcs.`}</p>
    <p>{`The arcs were supposed to use `}<inlineCode parentName="p">{`TransitionableComponent`}</inlineCode>{` to become animated, but … heh … it didn’t actually work as a library. Published to npm and everything. 29 people even tried to use it! Couldn’t even import ?`}</p>
    <p>{`Calling `}<inlineCode parentName="p">{`import TransitionableComponent from 'react-transitionable-component'`}</inlineCode>{` produced a syntax error. Something like `}<em parentName="p">{`“Unexpected token: export in index.js”`}</em>{`.`}</p>
    <p>{`When you publish a library, it has to be compiled. Nobody wants to build their whole node_modules directory every time they run Webpack, so configs always exclude it.`}</p>
    <p>{`I wonder how long build all your dependencies would take … I should try that one day ?`}</p>
    <p>{`So, to make a library work as a library, you have to compile it. To compile it, you have to first know how to run Webpack. It took me an embarrassing amount of time to realize that `}<inlineCode parentName="p">{`webpack webpack.config.prod.js`}</inlineCode>{` does `}<em parentName="p">{`not`}</em>{` run Webpack. `}<inlineCode parentName="p">{`webpack ––config=webpack.config.prod.js`}</inlineCode>{` does.`}</p>
    <p>{`To save some time, we modeled our webpack config after what we found in `}<a parentName="p" {...{
        "href": "https://github.com/facebookincubator/create-react-app"
      }}>{`react-scripts`}</a>{`. Also known as `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{`. When I say “model”, I mean “copy file then delete cruft”.`}</p>
    <p>{`You can delete everything to do with HTML and CSS. Unless your packaged component comes with its own styling, which I’m still on the fence about. Should open source components include stylings like jQuery plugins once did?`}</p>
    <p>{`Our final webpack config that got the AnimatedPiechart project running comes out to 74 lines and is too long to paste here. You can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-transitionable-component/blob/master/webpack.config.prod.js"
      }}>{`see it on Github`}</a>{`.`}</p>
    <p>{`We also had to install 16 dependencies, which is insane. And we’re no closer to knowing how to correctly use d3 v4 modularity and avoid depending on the whole library. ?`}</p>
    <p>{`But, you can use react-transition-group as a library now `}{`\\`}{`o/`}</p>
    <p>{`Join me next time, when we use `}<inlineCode parentName="p">{`TransitionableComponent`}</inlineCode>{` to build an animated piechart for real. Maybe even figure out how to do `}<inlineCode parentName="p">{`import Thing from 'd3-transform'`}</inlineCode>{` properly.`}</p>
    <p>{`PS: the edited and improved versions of these videos are becoming a video course. Readers of the engineer package of `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`React+d3js ES6`}</a>{` get the video course for free when it’s ready.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      